import { useEffect, useState } from 'react';
import Reveal from '../components/Reveal';
import HeroSlideshow from '../components/HeroSlideshow';
import SafeImage from '../components/SafeImage';
import './home.css';
import {
  DESTINATION_IMAGES,
  HERO_IMAGE,
  HERO_SLIDES,
  IMAGE_FALLBACK,
  SAFARI_PAGE_IMAGES,
} from '../data/destinationImages';
import {
  getGoogleProfileUrl,
  getGoogleWriteReviewUrl,
  getLocalBusinessReviewSchema,
} from '../data/googleBusiness';
import { getLocationById, getLocationsGroupedByRegion } from '../data/locations';
import { preloadImage } from '../utils/imageLoading';
import safarisCultural from '../assets/images/safaris-cultural.jpg';
import homeHeroCombo from '../assets/images/home-hero-13.jpg';

const REGION_LABELS = {
  northern: 'Northern Circuit',
  southern: 'Southern Circuit',
  western: 'Western Tanzania',
  coastal: 'Coast & Islands',
};

const REGION_ANCHORS = {
  northern: '#tanzania-mainland',
  southern: '#southern-circuit',
  coastal: '#tanzania-island',
  western: '#trekking',
};

/** Route ids match App.js hashes; locationId pulls copy from locations.js when set */
const NORTHERN_DESTINATIONS = [
  { id: 'serengeti', locationId: 'serengeti' },
  { id: 'ngorongoro', locationId: 'ngorongoro' },
  { id: 'tarangire', locationId: 'tarangire' },
  { id: 'manyara', locationId: 'lake-manyara', name: 'Lake Manyara National Park' },
  {
    id: 'arusha-park',
    name: 'Arusha National Park',
    summary:
      'Mount Meru views, Momella Lakes flamingos, and Ngurdoto Crater a compact park at the safari gateway.',
  },
];

const SOUTHERN_DESTINATIONS = [
  { id: 'mikumi', locationId: 'mikumi' },
  { id: 'nyerere', locationId: 'nyerere' },
  { id: 'ruaha', locationId: 'ruaha' },
];

const ISLAND_DESTINATIONS = [
  { id: 'zanzibar', locationId: 'zanzibar' },
];

const TREK_DESTINATIONS = [
  { id: 'kilimanjaro', locationId: 'kilimanjaro' },
  {
    id: 'meru',
    name: 'Mount Meru',
    summary:
      'Tanzania\'s second-highest peak a spectacular trek through forest, moorland, and volcanic crater rim.',
  },
];

const SAFARI_HIGHLIGHTS = [
  {
    title: 'Game drives',
    text: 'Dawn and dusk drives across Serengeti, Ngorongoro, Tarangire, and the southern parks.',
  },
  {
    title: 'Boat & walking safaris',
    text: 'Rufiji River cruises in Nyerere and guided bush walks in remote southern wilderness.',
  },
  {
    title: 'Balloon & migration',
    text: 'Hot-air balloon flights and Great Migration tracking on tailor-made itineraries.',
  },
];

const EXPERIENCE_CARDS = [
  {
    id: 'go-on-safari',
    title: 'Go on Safari',
    image: SAFARI_PAGE_IMAGES.gameDrive,
    paragraphs: [
      'A safari is a journey to watch and photograph wild animals in their natural home. With award-winning national parks, Tanzania is one of the world\'s finest safari destinations and Mori Tours turns that dream into a private, tailor-made adventure.',
      'We design customized safaris around your budget, travel dates, and travel style, carefully choosing lodges, camps, and routes that maximize wildlife viewing and comfort.',
      'We specialize in Northern Tanzania: Serengeti National Park, Ngorongoro Crater, Tarangire National Park, Lake Manyara National Park, and Arusha National Park. Choose a single day, a weekend escape, or a multi-day circuit combining parks so you experience the unique landscapes and wildlife of each place.',
      'Not sure where to begin? Browse our recommended safari itineraries then customize every detail with our team until the trip feels entirely yours.',
    ],
    ctaLabel: 'View safari experiences',
    ctaHref: '#safaris',
  },
  {
    id: 'climb-kilimanjaro-meru',
    title: 'Climb Mount Kilimanjaro & Meru',
    image: DESTINATION_IMAGES.kilimanjaro,
    paragraphs: [
      'Mount Kilimanjaro Africa\'s highest peak rises in Northern Tanzania. With the right preparation, climbers of all experience levels can reach the summit. You do not need to be a technical mountaineer; our guided private climbs focus on safety, pacing, and success.',
      'Once you choose your dates, the next step is your route. Kilimanjaro offers more than five distinct routes each with its own scenery, minimum duration, and challenge level. Mori Tours specializes in private climbs via Machame, Lemosho, Rongai, and Marangu, matching the trail to your fitness and goals.',
      'Prefer a shorter, equally spectacular climb? Mount Meru, Tanzania\'s second-highest peak offers rainforest, volcanic crater views, and outstanding acclimatization near Arusha. Many travellers climb Meru before Kilimanjaro, or enjoy Meru as a stand-alone trek.',
      'Not sure which mountain or route is right? Our experienced team will recommend a plan based on your fitness, hiking background, and preferences then build a customized itinerary that maximizes your chance of a successful summit.',
    ],
    ctaLabel: 'Explore climbing routes',
    ctaHref: '#kilimanjaro',
  },
  {
    id: 'zanzibar-island',
    title: 'Zanzibar Island Vacation',
    image: DESTINATION_IMAGES.zanzibar,
    paragraphs: [
      'Your Tanzania journey is incomplete without the Indian Ocean. Zanzibar part of the United Republic of Tanzania is a cluster of islands, mainly Unguja and Pemba, repeatedly named among Africa\'s best beach destinations. Expect award-winning beaches, Stone Town heritage, spice farms, cuisine, and water sports that stay with you long after you leave.',
      'We recommend at least three days on Zanzibar. Start by choosing what you want to do then we map the right beaches and excursions. Want dolphins? We\'ll include Mnemba Atoll or Kizimkazi. Prefer culture and history? Stone Town and spice tours take centre stage.',
      'From sample island itineraries to fully custom beach escapes, let us craft an unforgettable Zanzibar chapter of your Tanzania travel.',
    ],
    ctaLabel: 'Discover the islands',
    ctaHref: '#tanzania-island',
  },
  {
    id: 'day-trips',
    title: 'Day Trips',
    image: DESTINATION_IMAGES['arusha-park'] || DESTINATION_IMAGES.tarangire,
    paragraphs: [
      'Day trips are complete adventures you finish in a single day perfect weekend getaways and add-ons to longer Tanzania holidays.',
      'Popular Northern Tanzania day escapes include Arusha National Park, Ngorongoro Crater, Lake Manyara National Park, Tarangire National Park, Chemka Hot Springs, Materuni Waterfalls, Lake Chala, Olpopongi Maasai Village, and Marangu Chagga Village.',
      'We also arrange day visits to Empakai Crater, Lake Natron & Ol Doinyo Lengai viewpoints, coffee tours near Moshi, hot-air balloon morning flights (seasonal), and cultural half-days that pair beautifully with a city stay in Arusha.',
      'Tell us how much time you have we\'ll match you with the right day trip for wildlife, waterfalls, culture, or pure relaxation.',
    ],
    ctaLabel: 'Plan a day trip',
    ctaHref: '#contact',
  },
  {
    id: 'cultural-tours',
    title: 'Cultural Tours',
    image: safarisCultural,
    paragraphs: [
      'Tanzania is home to more than 120 tribes a living tapestry of languages, traditions, and ways of life. In the north, travellers often meet Maasai, Datoga, Hadzabe, and Chagga communities.',
      'Our cultural tours are immersive, respectful experiences. You learn about history, daily life, livelihoods, cuisine, traditional drinks, dance, and unique practices of the community you visit guided in a way that supports local hosts.',
      'Choose a single-day village visit or a multi-day cultural journey. Many guests blend culture with safari; others book stand-alone cultural tours. Either way, you leave with stories, friendships, and a deeper understanding of Tanzania beyond the parks.',
    ],
    ctaLabel: 'Ask about cultural tours',
    ctaHref: '#contact',
  },
  {
    id: 'combination',
    title: 'Combination',
    image: homeHeroCombo,
    paragraphs: [
      'The most memorable Tanzania trips combine experiences safari drama, mountain challenge, cultural connection, and island calm in one seamless journey.',
      'Imagine a few days of game drives, a cultural immersion day, then barefoot evenings on Zanzibar. Or arrive for Kilimanjaro, warm up with Materuni Waterfalls and Chagga culture (the first climbers of the mountain), summit Uhuru Peak, continue on safari, and finish with a beach escape on Zanzibar.',
      'Mount Kilimanjaro or Meru + safari, Southern Circuit + beach, or Northern parks + day trips we design combinations that flow naturally, with smart logistics and the right pace for you.',
      'Ready for a full Tanzania story? Explore how we blend destinations into one unforgettable itinerary.',
    ],
    ctaLabel: 'Build your combination',
    ctaHref: '#contact',
  },
];

const HOME_PACKAGES = [
  {
    image: SAFARI_PAGE_IMAGES.northern,
    title: 'Classic Northern Circuit',
    duration: '6–8 days',
    tag: 'Safari classic',
    text: 'Serengeti, Ngorongoro Crater, Tarangire, and Lake Manyara the definitive Tanzania safari across the parks that made the country famous.',
    highlights: ['Multi-park game drives', 'Crater descent', 'Migration windows'],
  },
  {
    image: DESTINATION_IMAGES.kilimanjaro,
    title: 'Kilimanjaro & Safari Combo',
    duration: '10–12 days',
    tag: 'Adventure',
    text: 'Summit Uhuru Peak on a private guided climb, then continue with game drives across the Northern Circuit mountain achievement and wildlife in one journey.',
    highlights: ['Machame or Lemosho route', 'Safari extension', 'Private logistics'],
  },
  {
    image: SAFARI_PAGE_IMAGES.southern,
    title: 'Southern Wilderness Safari',
    duration: '5–7 days',
    tag: 'Off the beaten path',
    text: 'Explore Mikumi, Nyerere, and Ruaha river safaris, fewer crowds, and predator country in Tanzania\'s wild south.',
    highlights: ['Boat safaris', 'Remote camps', 'Big cat territory'],
  },
  {
    image: DESTINATION_IMAGES.zanzibar,
    title: 'Safari & Zanzibar Finale',
    duration: '8–12 days',
    tag: 'Most popular',
    text: 'Pair mainland game drives with Stone Town culture and Indian Ocean beaches the classic Tanzania journey from wildlife plains to turquoise shores.',
    highlights: ['Safari + island', 'Stone Town stay', 'Beach lodge'],
  },
];

function resolveDestination(config) {
  const loc = config.locationId ? getLocationById(config.locationId) : null;
  const id = config.id ?? config.locationId;

  return {
    id,
    href: config.href ?? `#${id}`,
    name: config.name ?? loc?.name ?? id,
    summary: config.summary ?? loc?.summary ?? '',
    regionLabel: config.regionLabel ?? REGION_LABELS[loc?.regionId] ?? 'Tanzania',
    imageKey: config.imageKey ?? id,
  };
}

const STATS = [
  { value: '13+', label: 'Destinations' },
  { value: '4', label: 'Safari circuits' },
  { value: '100%', label: 'Tailor-made trips' },
  { value: '24/7', label: 'On-ground support' },
];

const STEPS = [
  {
    step: '01',
    title: 'Choose your destination',
    text: 'Browse parks, peaks, and islands across Tanzania every itinerary starts with where you want to go.',
  },
  {
    step: '02',
    title: 'Design your safari',
    text: 'We craft day-by-day plans around your dates, budget, and travel style lodge, camping, or luxury.',
  },
  {
    step: '03',
    title: 'Experience Tanzania',
    text: 'Travel with licensed guides, comfortable vehicles, and local expertise from Arusha to Zanzibar.',
  },
];

const WHY_US = [
  {
    title: 'Location-first planning',
    text: 'Tours built around real destinations not generic packages copied from a brochure.',
  },
  {
    title: 'Local expertise',
    text: 'Based in Arusha at the heart of the Northern Circuit, with guides who know every park.',
  },
  {
    title: 'Flexible itineraries',
    text: 'Safari only, Kilimanjaro trek, beach extension, or the full Tanzania journey your choice.',
  },
];

const CUSTOMER_REVIEWS = [
  {
    id: 'r1',
    name: 'Amelia & Thomas',
    origin: 'United Kingdom',
    rating: '5.0',
    trip: 'Northern Circuit Safari',
    quote:
      'Mori Tours planned every detail beautifully. Our guide knew exactly where to go, the lodges were perfect, and the Serengeti game drives were unforgettable.',
  },
  {
    id: 'r2',
    name: 'Nadia K.',
    origin: 'Germany',
    rating: '5.0',
    trip: 'Kilimanjaro & Safari Combo',
    quote:
      'The team helped us choose the right Kilimanjaro route and then arranged a smooth safari after the climb. We felt supported from arrival to departure.',
  },
  {
    id: 'r3',
    name: 'Jean-Pierre Family',
    origin: 'France',
    rating: '5.0',
    trip: 'Safari & Zanzibar Finale',
    quote:
      'A wonderful mix of wildlife, culture, and beach time. The itinerary moved at the right pace for our family and every transfer was handled on time.',
  },
];

const GOOGLE_WRITE_REVIEW_URL = getGoogleWriteReviewUrl();
const GOOGLE_PROFILE_URL = getGoogleProfileUrl();
const LOCAL_BUSINESS_REVIEW_SCHEMA = getLocalBusinessReviewSchema(
  CUSTOMER_REVIEWS.length,
);

function getStarString(rating) {
  const value = Math.round(Number(rating) || 0);
  const clamped = Math.max(0, Math.min(5, value));
  return '★'.repeat(clamped) + '☆'.repeat(5 - clamped);
}

function FeaturedDestinations({
  id,
  headingId,
  eyebrow,
  title,
  intro,
  destinationConfigs,
  gridClassName = '',
}) {
  const destinations = destinationConfigs.map(resolveDestination);

  return (
    <section id={id} className="home-section home-featured" aria-labelledby={headingId}>
      <div className="page-container">
        <Reveal className="home-section__header">
          <p className="home-section__eyebrow">{eyebrow}</p>
          <h2 id={headingId}>{title}</h2>
          <p className="home-section__intro">{intro}</p>
        </Reveal>
        <ul className={`home-featured__grid ${gridClassName}`.trim()}>
          {destinations.map((destination, index) => (
            <Reveal
              key={destination.id}
              as="li"
              className="home-featured__card"
              delay={index * 90}
            >
              <a href={destination.href} className="home-featured__link">
                <div className="home-featured__image">
                  <SafeImage
                    className="home-featured__img"
                    src={DESTINATION_IMAGES[destination.imageKey] || IMAGE_FALLBACK}
                    alt={destination.name}
                  />
                  <span className="home-featured__region">{destination.regionLabel}</span>
                </div>
                <div className="home-featured__body">
                  <h3>{destination.name}</h3>
                  <p>{destination.summary}</p>
                  <span className="home-featured__cta">View destination →</span>
                </div>
              </a>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Home() {
  const locationGroups = getLocationsGroupedByRegion();
  const [heroSlideIndex, setHeroSlideIndex] = useState(0);
  const heroSlide = HERO_SLIDES[heroSlideIndex] ?? HERO_SLIDES[0];

  useEffect(() => {
    preloadImage(HERO_IMAGE);
  }, []);

  return (
    <div className="home">
      <section className="home-hero" aria-labelledby="home-hero-heading">
        <div className="home-hero__media" aria-hidden="true">
          <HeroSlideshow
            slides={HERO_SLIDES}
            fallback={HERO_IMAGE}
            onSlideChange={setHeroSlideIndex}
          />
          <div className="home-hero__overlay" />
        </div>
        <div className="page-container home-hero__container">
          <div key={heroSlideIndex} className="home-hero__content">
            <p className="home-hero__eyebrow hero-text-item hero-text-item--1">
              {heroSlide.eyebrow}
            </p>
            <h1 id="home-hero-heading" className="hero-text-item hero-text-item--2">
              {heroSlide.title}
              <span> {heroSlide.titleAccent}</span>
            </h1>
            <p className="home-hero__lead hero-text-item hero-text-item--3">
              {heroSlide.lead}
            </p>
            <div className="home-hero__actions hero-text-item hero-text-item--4">
              <a className="home-btn home-btn--primary" href="#tanzania-mainland">
                Explore destinations
              </a>
              <a className="home-btn home-btn--outline" href="#contact">
                Plan your trip
              </a>
            </div>
          </div>
        </div>
        <div className="home-hero__stats">
          {STATS.map((stat, index) => (
            <div
              key={stat.label}
              className="home-hero__stat home-hero__stat--animate"
              style={{ '--stat-delay': `${index * 80}ms` }}
            >
              <span className="home-hero__stat-value">{stat.value}</span>
              <span className="home-hero__stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section
        id="what-to-do"
        className="home-section home-experiences"
        aria-labelledby="experiences-heading"
      >
        <div className="page-container">
          <Reveal className="home-section__header home-experiences__header">
            <p className="home-section__eyebrow">Plan your journey</p>
            <h2 id="experiences-heading">What would you like to do in Tanzania?</h2>
            <p className="home-section__intro">
              From classic safaris and summit climbs to island escapes, day trips, and
              cultural immersion, choose your adventure and we&apos;ll craft every detail
              around how you want to experience Tanzania.
            </p>
          </Reveal>
          <ul className="home-experiences__grid">
            {EXPERIENCE_CARDS.map((card, index) => (
              <Reveal
                key={card.id}
                as="li"
                className="home-experiences__card"
                delay={index * 80}
              >
                <div className="home-experiences__image">
                  <SafeImage
                    className="home-experiences__img"
                    src={card.image || IMAGE_FALLBACK}
                    alt={card.title}
                  />
                </div>
                <div className="home-experiences__body">
                  <h3>{card.title}</h3>
                  {card.paragraphs.map((paragraph) => (
                    <p key={paragraph.slice(0, 48)}>{paragraph}</p>
                  ))}
                  <a className="home-experiences__cta" href={card.ctaHref}>
                    {card.ctaLabel} →
                  </a>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="packages"
        className="home-section home-packages"
        aria-labelledby="packages-heading"
      >
        <div className="page-container">
          <Reveal className="home-section__header">
            <p className="home-section__eyebrow">Sample itineraries</p>
            <h2 id="packages-heading">Popular Tanzania packages</h2>
            <p className="home-section__intro">
              Ready-made starting points for safari, trekking, and island escapes
              each package is fully tailored to your dates, lodge style, and pace.
            </p>
          </Reveal>
          <ul className="home-packages__grid">
            {HOME_PACKAGES.map((pkg, index) => (
              <Reveal
                key={pkg.title}
                as="li"
                className="home-packages__card"
                delay={index * 100}
              >
                <div className="home-packages__card-image">
                  <SafeImage src={pkg.image} alt={pkg.title} />
                  <span className="home-packages__duration">{pkg.duration}</span>
                </div>
                <div className="home-packages__card-body">
                  <span className="home-packages__tag">{pkg.tag}</span>
                  <h3>{pkg.title}</h3>
                  <p>{pkg.text}</p>
                  <ul className="home-packages__highlights">
                    {pkg.highlights.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <a className="home-packages__cta" href="#contact">
                    Enquire about this package →
                  </a>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <FeaturedDestinations
        id="tanzania-mainland"
        headingId="northern-heading"
        eyebrow="Northern Safari Circuit"
        title="Classic wildlife & migration"
        intro="Serengeti, Ngorongoro, Tarangire, Lake Manyara, and Arusha National Park the parks that define the African safari."
        destinationConfigs={NORTHERN_DESTINATIONS}
        gridClassName="home-featured__grid--northern"
      />

      <FeaturedDestinations
        id="southern-circuit"
        headingId="southern-heading"
        eyebrow="Southern Safari Circuit"
        title="Remote parks & rainforest"
        intro="Mikumi, Nyerere, and Ruaha — fewer crowds, river safaris, and predator country in Tanzania's wild south."
        destinationConfigs={SOUTHERN_DESTINATIONS}
        gridClassName="home-featured__grid--southern"
      />

      <FeaturedDestinations
        id="tanzania-island"
        headingId="island-heading"
        eyebrow="Tanzania Island"
        title="Coast & islands"
        intro="White-sand beaches, Stone Town heritage, and spice-scented shores Zanzibar for the perfect post-safari escape."
        destinationConfigs={ISLAND_DESTINATIONS}
        gridClassName="home-featured__grid--islands"
      />

      <section
        id="explore-regions"
        className="home-section home-circuits"
        aria-labelledby="circuits-heading"
      >
        <div className="page-container">
          <Reveal className="home-section__header home-section__header--light" variant="fade">
            <p className="home-section__eyebrow">Explore by region</p>
            <h2 id="circuits-heading">Safari circuits &amp; zones</h2>
            <p className="home-section__intro">
              Tanzania is vast. We organize trips by circuit so you can focus on the
              landscapes and wildlife that matter most to you.
            </p>
          </Reveal>
          <ul className="home-circuits__grid">
            {locationGroups.map(({ region, locations }, index) => (
              <Reveal
                key={region.id}
                as="li"
                className="home-circuits__card"
                delay={index * 120}
                variant="scale"
              >
                <h3>{region.name}</h3>
                <p>{region.description}</p>
                <ul className="home-circuits__places">
                  {locations.slice(0, 5).map((loc) => (
                    <li key={loc.id}>{loc.name}</li>
                  ))}
                  {locations.length > 5 && (
                    <li className="home-circuits__more">+{locations.length - 5} more</li>
                  )}
                </ul>
                <a
                  href={REGION_ANCHORS[region.id] ?? '#contact'}
                  className="home-circuits__link"
                >
                  See {region.name.split(' ')[0]} destinations →
                </a>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        className="home-section home-safaris"
        aria-labelledby="safaris-preview-heading"
      >
        <div className="page-container">
          <div className="home-safaris__inner">
            <Reveal className="home-safaris__media" variant="right">
              <SafeImage
                className="home-safaris__img"
                src={SAFARI_PAGE_IMAGES.gameDrive}
                alt="Safari game drive in Tanzania"
              />
            </Reveal>
            <Reveal className="home-safaris__copy">
              <p className="home-section__eyebrow">Safari experiences</p>
              <h2 id="safaris-preview-heading">How we explore Tanzania</h2>
              <p className="home-section__intro">
                From classic game drives to balloon safaris and boat trips on the Rufiji
                every Mori Tours itinerary is built around how you want to experience the wild.
              </p>
              <ul className="home-safaris__highlights">
                {SAFARI_HIGHLIGHTS.map((item) => (
                  <li key={item.title}>
                    <strong>{item.title}</strong>
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
              <a className="home-btn home-btn--primary" href="#safaris">
                View all safari experiences
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      <section
        id="trekking"
        className="home-section home-trekking"
        aria-labelledby="trekking-heading"
      >
        <div className="page-container">
          <Reveal className="home-section__header">
            <p className="home-section__eyebrow">Mountains &amp; trails</p>
            <h2 id="trekking-heading">Kilimanjaro &amp; trekking adventures</h2>
            <p className="home-section__intro">
              Conquer Africa&apos;s highest peak or climb Mount Meru through rainforest
              and volcanic crater routes tailored to your fitness and timeline.
            </p>
          </Reveal>
          <ul className="home-trekking__grid">
            {TREK_DESTINATIONS.map(resolveDestination).map((destination, index) => (
              <Reveal
                key={destination.id}
                as="li"
                className="home-trekking__card"
                delay={index * 100}
              >
                <a href={destination.href} className="home-trekking__link">
                  <div className="home-trekking__image">
                    <SafeImage
                      className="home-trekking__img"
                      src={DESTINATION_IMAGES[destination.imageKey] || IMAGE_FALLBACK}
                      alt={destination.name}
                    />
                    <span className="home-trekking__badge">Trekking</span>
                  </div>
                  <div className="home-trekking__body">
                    <h3>{destination.name}</h3>
                    <p>{destination.summary}</p>
                    <span className="home-trekking__cta">View trek details →</span>
                  </div>
                </a>
              </Reveal>
            ))}
          </ul>
          <Reveal className="home-trekking__note" delay={200}>
            <p>
              Licensed guides, porters, and safety-first planning on Marangu, Machame,
              Lemosho, and Meru routes for summit treks on tailor-made itineraries.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="home-section home-steps" aria-labelledby="steps-heading">
        <div className="page-container">
          <Reveal className="home-section__header">
            <p className="home-section__eyebrow">Simple process</p>
            <h2 id="steps-heading">How your safari comes together</h2>
          </Reveal>
          <ol className="home-steps__list">
            {STEPS.map((item, index) => (
              <Reveal
                key={item.step}
                as="li"
                className="home-steps__item"
                delay={index * 150}
                variant="left"
              >
                <span className="home-steps__number">{item.step}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section id="about" className="home-section home-why" aria-labelledby="why-heading">
        <div className="page-container">
          <div className="home-why__inner">
            <Reveal className="home-why__copy" variant="right">
              <p className="home-section__eyebrow">Why Mori Tours</p>
              <h2 id="why-heading">Authentic Tanzania, planned with care</h2>
              <p>
                We are a Tanzania-based tour operator specializing in custom travel
                not one-size-fits-all group tours. Every itinerary reflects the places
                you want to see and the pace you want to travel.
              </p>
            </Reveal>
            <ul className="home-why__grid">
              {WHY_US.map((item, index) => (
                <Reveal
                  key={item.title}
                  as="li"
                  className="home-why__card"
                  delay={index * 120}
                >
                  <span className="home-why__icon" aria-hidden="true">✦</span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section
        className="home-section home-reviews"
        aria-labelledby="reviews-heading"
      >
        <div className="page-container">
          <Reveal className="home-section__header">
            <p className="home-section__eyebrow">Customer reviews</p>
            <h2 id="reviews-heading">Travellers who trusted Mori Tours</h2>
            <p className="home-section__intro">
              Real experiences from guests who explored Tanzania with our local planning,
              private guiding, and on-ground support.
            </p>
          </Reveal>
          <div className="home-reviews__google-wrap">
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(LOCAL_BUSINESS_REVIEW_SCHEMA),
              }}
            />
            <div className="home-reviews__google">
              <div className="home-reviews__google-copy">
                <span className="home-reviews__google-badge" aria-hidden="true">
                  <svg viewBox="0 0 24 24" role="img" aria-label="Google">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                </span>
                <h3 className="home-reviews__google-title">Leave a review on Google</h3>
                <p className="home-reviews__google-text">
                  Share your Mori Tours experience on Google. Verified reviews help other
                  travellers plan with confidence and improve our visibility in search.
                </p>
              </div>
              <div className="home-reviews__google-actions">
                <a
                  className="home-reviews__google-btn home-reviews__google-btn--primary"
                  href={GOOGLE_WRITE_REVIEW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Write a Google review
                </a>
                <a
                  className="home-reviews__google-btn home-reviews__google-btn--secondary"
                  href={GOOGLE_PROFILE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View on Google Maps
                </a>
              </div>
            </div>
          </div>
          <ul className="home-reviews__grid">
            {CUSTOMER_REVIEWS.map((review, index) => (
              <Reveal
                key={review.id ?? `${review.name}-${review.trip}-${index}`}
                as="li"
                className="home-reviews__card"
                delay={index * 120}
              >
                <div className="home-reviews__rating" aria-label={`${review.rating} out of 5 stars`}>
                  <span aria-hidden="true">{getStarString(review.rating)}</span>
                  <strong>{review.rating}</strong>
                </div>
                <blockquote>“{review.quote}”</blockquote>
                <div className="home-reviews__author">
                  <strong>{review.name}</strong>
                  <span>{review.origin}</span>
                </div>
                <span className="home-reviews__trip">{review.trip}</span>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="home-cta" aria-labelledby="cta-heading">
        <div className="page-container">
          <Reveal variant="scale" delay={0}>
            <div className="home-cta__inner">
              <h2 id="cta-heading">Ready to explore Tanzania?</h2>
              <p>
                Tell us your dream destinations and travel dates we&apos;ll design a
                private safari quote within 24 hours.
              </p>
              <a className="home-btn home-btn--primary home-btn--lg" href="#contact">
                Get a free quote
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

export default Home;
